import { useQuery } from "@tanstack/react-query";
import {
  ArrowUpRight,
  BarChart3,
  Calendar,
  CreditCard,
  Download,
  Package,
  TrendingUp,
  UserCheck,
  Warehouse,
} from "lucide-react";
import { useState } from "react";

import { Button, Card, Money, StatusPill } from "@/components/ui";
import {
  downloadSalesCsv,
  getCashierPerformance,
  getInventoryValuation,
  getSalesByPeriod,
  getSalesSummary,
  getTopProducts,
} from "@/lib/reports";
import { getSession } from "@/lib/session";

export function ReportsScreen() {
  const session = getSession();
  const branchId = session?.branches?.[0]?.id ?? "";

  // Helper fechas
  const today = new Date().toISOString().slice(0, 10);
  const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);
  const firstDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1)
    .toISOString()
    .slice(0, 10);

  const [dateRange, setDateRange] = useState<"today" | "week" | "month" | "custom">("month");
  const [startDate, setStartDate] = useState(firstDayOfMonth);
  const [endDate, setEndDate] = useState(today);
  const [activeTab, setActiveTab] = useState<"timeline" | "products" | "cashiers" | "inventory">(
    "timeline"
  );
  const [isExporting, setIsExporting] = useState(false);

  function handleSelectPreset(preset: "today" | "week" | "month") {
    setDateRange(preset);
    if (preset === "today") {
      setStartDate(today);
      setEndDate(today);
    } else if (preset === "week") {
      setStartDate(sevenDaysAgo);
      setEndDate(today);
    } else if (preset === "month") {
      setStartDate(firstDayOfMonth);
      setEndDate(today);
    }
  }

  // Queries
  const { data: summaryData } = useQuery({
    queryKey: ["reports", "summary", branchId, startDate, endDate],
    queryFn: () =>
      getSalesSummary({
        branch_id: branchId || undefined,
        start_date: startDate,
        end_date: endDate,
      }),
  });

  const { data: timelineData } = useQuery({
    queryKey: ["reports", "timeline", branchId, startDate, endDate],
    queryFn: () =>
      getSalesByPeriod({
        branch_id: branchId || undefined,
        start_date: startDate,
        end_date: endDate,
      }),
    enabled: activeTab === "timeline",
  });

  const { data: topProductsData } = useQuery({
    queryKey: ["reports", "products", branchId, startDate, endDate],
    queryFn: () =>
      getTopProducts({
        branch_id: branchId || undefined,
        start_date: startDate,
        end_date: endDate,
        limit: 25,
      }),
    enabled: activeTab === "products",
  });

  const { data: cashiersData } = useQuery({
    queryKey: ["reports", "cashiers", branchId, startDate, endDate],
    queryFn: () =>
      getCashierPerformance({
        branch_id: branchId || undefined,
        start_date: startDate,
        end_date: endDate,
      }),
    enabled: activeTab === "cashiers",
  });

  const { data: valuationData } = useQuery({
    queryKey: ["reports", "valuation"],
    queryFn: () => getInventoryValuation(),
    enabled: activeTab === "inventory",
  });

  async function handleExportCsv() {
    try {
      setIsExporting(true);
      await downloadSalesCsv({
        branch_id: branchId || undefined,
        start_date: startDate,
        end_date: endDate,
      });
    } catch (err) {
      console.error(err);
      alert("Error al exportar reporte de ventas.");
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <div className="flex h-full flex-col overflow-y-auto bg-surface p-6">
      {/* Encabezado y controles de fecha */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-carbon">
            Reportes y Analítica
          </h1>
          <p className="text-sm text-muted">
            Métricas de utilidad bruta, ventas en tiempo real, valoración de stock y rendimiento.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Presets */}
          <div className="flex rounded-msr border border-line bg-snow p-1 text-xs">
            <button
              onClick={() => handleSelectPreset("today")}
              className={`rounded px-3 py-1 font-medium transition-colors ${
                dateRange === "today" ? "bg-electric text-white" : "text-muted hover:text-carbon"
              }`}
            >
              Hoy
            </button>
            <button
              onClick={() => handleSelectPreset("week")}
              className={`rounded px-3 py-1 font-medium transition-colors ${
                dateRange === "week" ? "bg-electric text-white" : "text-muted hover:text-carbon"
              }`}
            >
              7 Días
            </button>
            <button
              onClick={() => handleSelectPreset("month")}
              className={`rounded px-3 py-1 font-medium transition-colors ${
                dateRange === "month" ? "bg-electric text-white" : "text-muted hover:text-carbon"
              }`}
            >
              Mes en Curso
            </button>
          </div>

          {/* Selector rango personalizado */}
          <div className="flex items-center gap-2 rounded-msr border border-line bg-snow px-3 py-1 text-xs text-carbon">
            <Calendar className="h-3.5 w-3.5 text-muted" />
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setDateRange("custom");
              }}
              className="border-none bg-transparent focus:outline-none"
            />
            <span className="text-muted">al</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setDateRange("custom");
              }}
              className="border-none bg-transparent focus:outline-none"
            />
          </div>

          <Button
            variant="secondary"
            onClick={handleExportCsv}
            disabled={isExporting}
            className="flex items-center gap-2 text-xs"
          >
            <Download className="h-4 w-4" />
            {isExporting ? "Generando..." : "Descargar CSV"}
          </Button>
        </div>
      </div>

      {/* Tarjetas KPI Resumen Ejecutivo */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* Venta Neta */}
        <div className="rounded-msr-lg border border-line bg-snow p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase text-muted">Venta Neta Total</span>
            <div className="rounded-full bg-electric/10 p-2 text-electric">
              <TrendingUp className="h-4 w-4" />
            </div>
          </div>
          <Money
            cents={summaryData?.net_sales_cents ?? 0}
            size="xl"
            className="mt-3 font-bold text-carbon"
          />
          <div className="mt-1 flex items-center gap-1.5 text-xs text-muted">
            <span>{summaryData?.total_sales_count ?? 0} tickets cobrados</span>
          </div>
        </div>

        {/* Utilidad Bruta y Margen */}
        <div className="rounded-msr-lg border border-line bg-snow p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase text-muted">Utilidad Bruta</span>
            <div className="rounded-full bg-success-soft p-2 text-success">
              <ArrowUpRight className="h-4 w-4" />
            </div>
          </div>
          <Money
            cents={summaryData?.gross_profit_cents ?? 0}
            size="xl"
            className="mt-3 font-bold text-success"
          />
          <div className="mt-1 flex items-center gap-2 text-xs">
            <StatusPill tone="ok" className="text-[11px]">
              {summaryData?.margin_percentage ?? 0}% Margen
            </StatusPill>
            <span className="text-muted">sobre venta</span>
          </div>
        </div>

        {/* Costo Mercancía Vendida */}
        <div className="rounded-msr-lg border border-line bg-snow p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase text-muted">Costo Mercancía (COGS)</span>
            <div className="rounded-full bg-warning-soft p-2 text-warning">
              <CreditCard className="h-4 w-4" />
            </div>
          </div>
          <Money
            cents={summaryData?.cost_cents ?? 0}
            size="xl"
            className="mt-3 font-bold text-carbon"
          />
          <div className="mt-1 text-xs text-muted">Costo promedio registrado</div>
        </div>

        {/* Ticket Promedio */}
        <div className="rounded-msr-lg border border-line bg-snow p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase text-muted">Ticket Promedio</span>
            <div className="rounded-full bg-electric/10 p-2 text-electric">
              <BarChart3 className="h-4 w-4" />
            </div>
          </div>
          <Money
            cents={summaryData?.average_ticket_cents ?? 0}
            size="xl"
            className="mt-3 font-bold text-carbon"
          />
          <div className="mt-1 text-xs text-muted">Por cliente / transacción</div>
        </div>
      </div>

      {/* Pestañas de detalle */}
      <div className="mb-4 flex border-b border-line gap-4 text-sm font-semibold">
        <button
          onClick={() => setActiveTab("timeline")}
          className={`flex items-center gap-2 border-b-2 pb-3 transition-colors ${
            activeTab === "timeline"
              ? "border-electric text-electric-text font-bold"
              : "border-transparent text-muted hover:text-carbon"
          }`}
        >
          <Calendar className="h-4 w-4" />
          Ventas en el Tiempo
        </button>
        <button
          onClick={() => setActiveTab("products")}
          className={`flex items-center gap-2 border-b-2 pb-3 transition-colors ${
            activeTab === "products"
              ? "border-electric text-electric-text font-bold"
              : "border-transparent text-muted hover:text-carbon"
          }`}
        >
          <Package className="h-4 w-4" />
          Top Productos y Margen
        </button>
        <button
          onClick={() => setActiveTab("cashiers")}
          className={`flex items-center gap-2 border-b-2 pb-3 transition-colors ${
            activeTab === "cashiers"
              ? "border-electric text-electric-text font-bold"
              : "border-transparent text-muted hover:text-carbon"
          }`}
        >
          <UserCheck className="h-4 w-4" />
          Desempeño de Cajeros
        </button>
        <button
          onClick={() => setActiveTab("inventory")}
          className={`flex items-center gap-2 border-b-2 pb-3 transition-colors ${
            activeTab === "inventory"
              ? "border-electric text-electric-text font-bold"
              : "border-transparent text-muted hover:text-carbon"
          }`}
        >
          <Warehouse className="h-4 w-4" />
          Valoración de Almacenes
        </button>
      </div>

      {/* Contenido pestaña 1: Ventas en el tiempo */}
      {activeTab === "timeline" && (
        <Card className="flex-1 overflow-hidden flex flex-col">
          <div className="overflow-x-auto flex-1">
            <table className="w-full border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-line bg-surface/50 text-xs font-semibold uppercase text-muted">
                  <th className="p-4">Fecha</th>
                  <th className="p-4">Transacciones</th>
                  <th className="p-4">Venta Total</th>
                  <th className="p-4">Costo Mercancía</th>
                  <th className="p-4 text-right">Utilidad Bruta</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {timelineData?.items.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="p-8 text-center text-muted">
                      No se registraron ventas en el período seleccionado.
                    </td>
                  </tr>
                ) : (
                  timelineData?.items.map((row) => (
                    <tr key={row.period} className="hover:bg-surface/30">
                      <td className="p-4 font-mono font-medium text-carbon">{row.period}</td>
                      <td className="p-4 text-muted">{row.sales_count} ventas</td>
                      <td className="p-4 font-semibold text-carbon">
                        <Money cents={row.total_cents} />
                      </td>
                      <td className="p-4 text-muted">
                        <Money cents={row.cost_cents} />
                      </td>
                      <td className="p-4 text-right font-bold text-success">
                        <Money cents={row.gross_profit_cents} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Contenido pestaña 2: Top Productos */}
      {activeTab === "products" && (
        <Card className="flex-1 overflow-hidden flex flex-col">
          <div className="overflow-x-auto flex-1">
            <table className="w-full border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-line bg-surface/50 text-xs font-semibold uppercase text-muted">
                  <th className="p-4">SKU</th>
                  <th className="p-4">Producto / Variante</th>
                  <th className="p-4">Piezas Vendidas</th>
                  <th className="p-4">Ingresos Brutos</th>
                  <th className="p-4">Costo Total</th>
                  <th className="p-4">Utilidad</th>
                  <th className="p-4 text-right">Margen %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {topProductsData?.items.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-muted">
                      No hay datos de productos en este rango.
                    </td>
                  </tr>
                ) : (
                  topProductsData?.items.map((item) => (
                    <tr key={item.variant_id} className="hover:bg-surface/30">
                      <td className="p-4 font-mono text-xs text-muted">{item.sku}</td>
                      <td className="p-4 font-medium text-carbon">{item.name}</td>
                      <td className="p-4 font-mono font-semibold">{item.quantity_sold}</td>
                      <td className="p-4 font-semibold text-carbon">
                        <Money cents={item.revenue_cents} />
                      </td>
                      <td className="p-4 text-muted">
                        <Money cents={item.cost_cents} />
                      </td>
                      <td className="p-4 font-bold text-success">
                        <Money cents={item.gross_profit_cents} />
                      </td>
                      <td className="p-4 text-right">
                        <StatusPill tone="ok">{item.margin_percentage}%</StatusPill>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Contenido pestaña 3: Cajeros */}
      {activeTab === "cashiers" && (
        <Card className="flex-1 overflow-hidden flex flex-col">
          <div className="overflow-x-auto flex-1">
            <table className="w-full border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-line bg-surface/50 text-xs font-semibold uppercase text-muted">
                  <th className="p-4">Cajero / Usuario</th>
                  <th className="p-4">Tickets Cobrados</th>
                  <th className="p-4">Venta Total Acumulada</th>
                  <th className="p-4 text-right">Ticket Promedio</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {cashiersData?.items.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="p-8 text-center text-muted">
                      No hay transacciones registradas por cajeros.
                    </td>
                  </tr>
                ) : (
                  cashiersData?.items.map((c) => (
                    <tr key={c.user_name} className="hover:bg-surface/30">
                      <td className="p-4 font-medium text-carbon">{c.user_name}</td>
                      <td className="p-4 text-muted">{c.sales_count} ventas</td>
                      <td className="p-4 font-bold text-carbon">
                        <Money cents={c.total_cents} />
                      </td>
                      <td className="p-4 text-right font-medium">
                        <Money cents={c.average_ticket_cents} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Contenido pestaña 4: Valoración de Almacenes */}
      {activeTab === "inventory" && (
        <div className="flex-1 flex flex-col gap-4">
          <div className="rounded-msr-lg border border-line bg-snow p-4 flex items-center justify-between">
            <div>
              <span className="text-xs font-semibold uppercase text-muted">
                Valoración Total de Inventario en Existencia
              </span>
              <div className="text-sm text-muted">
                {valuationData?.total_items_count ?? 0} productos activos con saldo positivo
              </div>
            </div>
            <Money
              cents={valuationData?.total_valuation_cents ?? 0}
              size="xl"
              className="font-bold text-electric"
            />
          </div>

          <Card className="flex-1 overflow-hidden flex flex-col">
            <div className="overflow-x-auto flex-1">
              <table className="w-full border-collapse text-left text-sm">
                <thead>
                  <tr className="border-b border-line bg-surface/50 text-xs font-semibold uppercase text-muted">
                    <th className="p-4">Almacén</th>
                    <th className="p-4">SKU</th>
                    <th className="p-4">Producto</th>
                    <th className="p-4">Existencia</th>
                    <th className="p-4">Costo Promedio Ponderado</th>
                    <th className="p-4 text-right">Valor Total Stock</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {valuationData?.items.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-muted">
                        No hay existencias en almacenes.
                      </td>
                    </tr>
                  ) : (
                    valuationData?.items.map((v) => (
                      <tr key={`${v.warehouse_id}-${v.variant_id}`} className="hover:bg-surface/30">
                        <td className="p-4 font-medium text-carbon">{v.warehouse_name}</td>
                        <td className="p-4 font-mono text-xs text-muted">{v.sku}</td>
                        <td className="p-4 font-semibold text-carbon">{v.product_name}</td>
                        <td className="p-4 font-mono font-medium">{v.quantity}</td>
                        <td className="p-4 text-muted">
                          <Money cents={v.avg_cost_cents} />
                        </td>
                        <td className="p-4 text-right font-bold text-carbon">
                          <Money cents={v.total_valuation_cents} />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
